import { Badge, EmptyState, Surface } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import type { AuditEntryDto, AuditHistoryResponse, EventTopic } from '@warehouse/shared';
import { History, Smartphone } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../../../lib/format';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

/**
 * The "Historik" tab (DATA-MODEL: `audit_log`, index `(entity, entity_id, at desc)`).
 *
 * It reads the rows CommandBus wrote INSIDE the transaction that made the
 * change, so this list and the record it describes can never disagree — the
 * property the old WMS lacked, where picking wrote no history at all.
 *
 * Only changed fields are shown, because `diffChanges` only stores changed
 * fields: a full row snapshot per save makes the tab unreadable within a week.
 */
export function EntityHistory({
  queryKey,
  fetch,
  topics,
}: {
  queryKey: readonly unknown[];
  fetch: () => Promise<AuditHistoryResponse>;
  topics: readonly EventTopic[];
}): ReactNode {
  const { t } = useTranslation('masterData');
  const { data, isLoading } = useQuery({ queryKey: [...queryKey], queryFn: fetch });
  useLiveQuery(topics, [...queryKey]);

  const items = data?.items ?? [];

  if (!isLoading && items.length === 0) {
    return (
      <EmptyState icon={<History />} title={t('history.empty')} description={t('history.emptyDescription')} />
    );
  }

  return (
    <Surface className="divide-y divide-surface-border">
      {items.map((entry) => (
        <HistoryRow key={entry.id} entry={entry} />
      ))}
    </Surface>
  );
}

function HistoryRow({ entry }: { entry: AuditEntryDto }): ReactNode {
  const { t } = useTranslation('masterData');
  const changed = Object.keys(entry.changes?.after ?? {});

  return (
    <article className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-start sm:gap-6">
      <div className="w-44 shrink-0">
        <p className="font-mono text-xs text-ink-muted">{formatDateTime(entry.at)}</p>
        <p className="text-sm text-ink">{entry.userName ?? t('history.system')}</p>
        {entry.deviceId ? (
          <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-ink-muted">
            <Smartphone className="size-3" aria-hidden />
            {t('history.device')}
          </p>
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        <Badge tone="neutral" size="sm">
          {entry.action}
        </Badge>

        {changed.length === 0 ? (
          <p className="mt-2 text-sm text-ink-muted">{t('history.noChanges')}</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-1">
            {changed.map((field) => (
              <li key={field} className="text-sm text-ink-light">
                <span className="font-mono text-xs text-ink-muted">{field}</span>{' '}
                <span className="text-ink-muted">{t('history.from')}</span>{' '}
                <Value value={entry.changes?.before?.[field]} />{' '}
                <span className="text-ink-muted">{t('history.to')}</span>{' '}
                <Value value={entry.changes?.after?.[field]} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

function Value({ value }: { value: unknown }): ReactNode {
  const { t } = useTranslation('masterData');
  if (value === null || value === undefined || value === '') {
    return <em className="text-ink-muted">{t('history.empty_value')}</em>;
  }
  if (typeof value === 'boolean') return <strong>{value ? 'true' : 'false'}</strong>;
  if (typeof value === 'object') return <code className="text-xs">{JSON.stringify(value)}</code>;
  return <strong>{String(value)}</strong>;
}
